import type { Customer, Item, ItemFieldEntry } from '@cheqroom/api';
import { createContext, FC, PropsWithChildren, useContext } from 'react';

import { type InfoConfig, useInfoConfig } from '../Info/Info.context';

export type ItemInfoItem = Pick<
	Item,
	| '_id'
	| 'kind'
	| 'codes'
	| 'barcodes'
	| 'status'
	| 'canReserve'
	| 'canOrder'
	| 'orderQuantitiesWithKits'
	| 'quantity'
	| 'kitQuantities'
	| 'inKits'
	| 'flag'
	| 'itemTypeId'
> & {
	kit?: Pick<NonNullable<Item['kit']>, '_id' | 'name'>;
	custody?: Pick<NonNullable<Item['custody']>, 'name' | '_id'>;
	order?: { customer?: Pick<Customer, 'name'> | null } | null;
	location?: Pick<NonNullable<Item['location']>, 'name' | 'icon'>;
	locationPath?: Pick<NonNullable<Item['location']>, 'name'>[];
	fields?: Record<string, unknown>;
};

export interface ItemInfoItemValue {
	item: ItemInfoItem;
	fieldDefs?: ItemFieldEntry[];
}

const ItemInfoItemContext = createContext<ItemInfoItemValue | null>(null);

/** Internal — mounted by `ItemInfo`, not part of the public API. */
export const ItemInfoItemProvider: FC<PropsWithChildren<ItemInfoItemValue>> = ({ children, ...value }) => (
	<ItemInfoItemContext.Provider value={value}>{children}</ItemInfoItemContext.Provider>
);

const useItemInfoItem = (): ItemInfoItemValue => {
	const value = useContext(ItemInfoItemContext);
	if (value == null) {
		throw new Error('useItemInfoContext was used outside of ItemInfo');
	}
	return value;
};

/** Used by every internal descendant (FieldRenderer, Status, Location, Flag, PartOfKit) — reads
 * both contexts and combines them into the single shape they've always consumed. */
export const useItemInfoContext = (): InfoConfig & ItemInfoItemValue => ({
	...useInfoConfig(),
	...useItemInfoItem(),
});
